import firebase from "firebase/app";
import "firebase/auth";
import TokenHelper from "../helpers/TokenHelper";
import RequestHelper from "../helpers/RequestHelper";
import DateTimeHelper from "../core/DateTimeHelper";
import firebaseConfig from "../firebaseConfig";
import { ExpiredRefreshTokenError } from "../core/CustomErrors";

let sessionRecovery = null;

function ensureFirebaseAuth() {
  if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
  }
  return firebase.auth();
}

function restoredFirebaseUser() {
  const auth = ensureFirebaseAuth();
  if (auth.currentUser) return Promise.resolve(auth.currentUser);

  return new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        unsubscribe();
        resolve(user);
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });
}

function beginSessionRecovery(tokenService) {
  if (!sessionRecovery) {
    sessionRecovery = tokenService.recoverSession().finally(() => {
      sessionRecovery = null;
    });
  }
  return sessionRecovery;
}

export default class TokenService {
  #_requestHelper;
  #_tokenHelper;
  #_dateTimeHelper;

  constructor() {
    this._requestHelper = new RequestHelper();
    this._tokenHelper = new TokenHelper();
    this._dateTimeHelper = new DateTimeHelper();
  }

  async initialiseAuthTokens(idToken) {
    console.log("TokenService.initialiseAuthTokens");

    try {
      await this.establishSession(idToken);
    } catch (err) {
      throw new Error("Failed to initialise authorisation tokens");
    }
  }

  async checkAccessToken() {
    if (this.accessTokenIsUsable()) {
      console.log("TokenService: Access Token valid");
      return;
    }

    console.log("TokenService: Access Token expired");
    return beginSessionRecovery(this);
  }

  async recoverSession() {
    if (this.accessTokenIsUsable()) return;

    if (this.refreshTokenIsUsable()) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed || this.accessTokenIsUsable()) return;
      console.log("TokenService: Refresh rejected, restoring session from Firebase");
    } else {
      console.log("TokenService: Refresh token expired, restoring session from Firebase");
    }

    await this.signInFromFirebaseSession();
  }

  async establishSession(idToken) {
    const route = "/boom/login";
    const response = await this._requestHelper.getRequestResponse(route, idToken, "POST");
    console.log("TokenService " + route + " response: " + response.status);

    if (!response.ok) {
      const error = new Error("Failed to initialise authorisation tokens");
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    this._tokenHelper.setAuthTokens(data[0]);
  }

  accessTokenIsUsable() {
    try {
      return !this._tokenHelper.hasAccessTokenExpired();
    } catch (err) {
      return false;
    }
  }

  refreshTokenIsUsable() {
    try {
      return !this._tokenHelper.hasRefreshTokenExpired();
    } catch (err) {
      return false;
    }
  }

  async refreshAccessToken() {
    const route = "/boom/refresh";
    const response = await this._requestHelper.getRequestResponse(
      route,
      this._tokenHelper.getRefreshToken(),
      "POST"
    );
    console.log("TokenService " + route + " response: " + response.status);

    if (!response.ok) return false;

    const data = await response.json();
    this._tokenHelper.setAuthTokens(data[0]);
    console.log(
      "TokenService: Access Token refreshed. Expiration = " +
        this._tokenHelper.getAccessTokenExpiration().toUTCString()
    );
    return true;
  }

  async signInFromFirebaseSession() {
    const user = await restoredFirebaseUser();
    if (!user) throw new ExpiredRefreshTokenError();

    try {
      const idToken = await user.getIdToken();
      await this.establishSession(idToken);
    } catch (err) {
      if (err.status === 401) throw new ExpiredRefreshTokenError();
      throw err;
    }
  }
}
